import { toApiError } from '@practium/features/api';
import { useToast } from '@practium/ui';
import { useCallback, useEffect, useState } from 'react';
import {
	UpdatePolicy,
	useActivatePolicy,
	useDeletePolicy,
	useDisablePolicy,
	useGetManagementePolicyVersion,
	usePostPolicy,
	useUpdatePolicy,
} from '../../api/hooks/policy-versions';
import { Policy, PolicyFilters } from '../types/policy.types';

export const usePolicies = () => {
	const { data = [], isLoading: isLoadingPolicies } = useGetManagementePolicyVersion();
	const { mutateAsync: activatePolicy } = useActivatePolicy();
	const { mutateAsync: disablePolicy } = useDisablePolicy();
	const { mutateAsync: createPolicy } = usePostPolicy();
	const { mutateAsync: deletePolicyById } = useDeletePolicy();
	const { mutateAsync: updatePolicyById } = useUpdatePolicy();
	const [policies, setPolicies] = useState<Policy[]>([]);
	const [filteredPolicies, setFilteredPolicies] = useState<Policy[]>([]);
	const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
	const [updatingPolicyId, setUpdatingPolicyId] = useState<string | null>(null);
	const [filters, setFilters] = useState<PolicyFilters>({
		searchQuery: '',
		policyType: undefined,
	});
	const toast = useToast();

	useEffect(() => {
		const applyFilters = () => {
			let filtered = [...policies];

			if (filters.searchQuery) {
				const query = filters.searchQuery.toLowerCase().trim();
				if (query) {
					filtered = filtered.filter(
						(policy) =>
							(policy.title || '').toLowerCase().includes(query) ||
							(policy.description || '').toLowerCase().includes(query) ||
							policy.versionNumber.includes(query)
					);
				}
			}

			if (filters.policyType) {
				filtered = filtered.filter((policy) => policy.policyType === filters.policyType);
			}

			setFilteredPolicies(filtered);
		};

		applyFilters();
	}, [filters, policies]);

	useEffect(() => {
		setPolicies(data);
	}, [data]);

	const handleSearch = useCallback((query: string) => {
		const sanitizedQuery = query.trim();
		setFilters((prev) => ({ ...prev, searchQuery: sanitizedQuery }));
	}, []);

	const handleTogglePolicy = useCallback(
		async (policyId: string, isActive: boolean) => {
			setUpdatingPolicyId(policyId);
			try {
				if (isActive) {
					if (!selectedPolicy) return;

					await activatePolicy({ id: policyId, policyType: selectedPolicy.policyType });
				} else {
					await disablePolicy({ id: policyId });
				}
			} catch (error) {
				if (error instanceof Error) toast.error(error.message);
			} finally {
				setUpdatingPolicyId(null);
			}
		},
		[selectedPolicy, activatePolicy, disablePolicy]
	);

	const selectPolicy = useCallback((policy: Policy) => {
		setSelectedPolicy(policy);
	}, []);

	const saveNewPolicy = useCallback(
		async (newPolicy: Policy) => {
			try {
				const savedPolicy = await createPolicy(newPolicy);
				toast.success('Política criada com sucesso!');
				return savedPolicy;
			} catch (error) {
				if (error instanceof Error) toast.error(error.message);
				return undefined;
			}
		},
		[createPolicy]
	);

	const addTemporaryPolicy = useCallback((policy: Policy) => {
		setSelectedPolicy(policy);
	}, []);

	const updateTemporaryPolicy = useCallback((policyId: string, updates: Partial<Policy>) => {
		setPolicies((prev) => prev.map((p) => (p._id === policyId ? { ...p, ...updates } : p)));
		setSelectedPolicy((prev) => (prev?._id === policyId ? { ...prev, ...updates } : prev));
	}, []);

	const removeTemporaryPolicy = useCallback((policyId: string) => {
		setPolicies((prev) => prev.filter((p) => p._id !== policyId));
		setSelectedPolicy((prev) => (prev?._id === policyId ? null : prev));
	}, []);

	const updatePolicy = useCallback(
		async (data: UpdatePolicy) => {
			try {
				const updatedPolicy = await updatePolicyById(data);
				toast.success('Política atualizada com sucesso!');
				if (selectedPolicy?._id === data.policyVersionId) {
					setSelectedPolicy((prev) => (prev ? { ...prev, ...data } : null));
				}
				return updatedPolicy;
			} catch (error) {
				throw toApiError(error, 'Erro ao atualizar o termo.');
			} finally {
				setUpdatingPolicyId(null);
			}
		},
		[updatePolicyById]
	);

	const deletePolicy = useCallback(
		async (policyId: string) => {
			try {
				const policy = await deletePolicyById(policyId);
				toast.success('Política foi removida!');
				return policy;
			} catch (error) {
				throw toApiError(error, 'Erro ao apagar o termo.');
			}
		},
		[deletePolicyById]
	);

	return {
		policies,
		filteredPolicies,
		selectedPolicy,
		isLoading: isLoadingPolicies,
		updatingPolicyId,
		filters,
		handleSearch,
		handleTogglePolicy,
		selectPolicy,
		saveNewPolicy,
		addTemporaryPolicy,
		updateTemporaryPolicy,
		removeTemporaryPolicy,
		updatePolicy,
		deletePolicy,
	};
};
